import { useCallback, useEffect, useMemo, useState } from "react";
import AdminCard from "../components/admin/AdminCard";
import AdminTable from "../components/admin/AdminTable";
import Modal from "../components/common/Modal";
import Button from "../components/common/Button";
import Input from "../components/common/Input";
import Loader from "../components/common/Loader";
import { formatDate, formatDateForApi } from "../utils/formatDate";
import {
  isEmpty,
  isEndTimeAfterStartTime,
  isTimeIncrementValid,
} from "../utils/validators";
import * as availabilityService from "../services/availabilityService";

const initialFormState = {
  id: null,
  date: "",
  startTime: "",
  endTime: "",
  isActive: true,
};

const getTodayLocalDateString = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = `${today.getMonth() + 1}`.padStart(2, "0");
  const day = `${today.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const isPastAvailabilityDate = (date) => {
  if (!date) return false;
  return date < getTodayLocalDateString();
};

const safeCallGetAllAvailability = async () => {
  if (typeof availabilityService.getAllAvailabilityRules === "function") {
    return availabilityService.getAllAvailabilityRules();
  }

  if (typeof availabilityService.getAvailabilityRules === "function") {
    return availabilityService.getAvailabilityRules();
  }

  if (typeof availabilityService.getAvailabilities === "function") {
    return availabilityService.getAvailabilities();
  }

  throw new Error("Availability list method was not found in availabilityService.");
};

const safeCallCreateAvailability = async (payload) => {
  if (typeof availabilityService.createAvailabilityRule === "function") {
    return availabilityService.createAvailabilityRule(payload);
  }

  if (typeof availabilityService.addAvailabilityRule === "function") {
    return availabilityService.addAvailabilityRule(payload);
  }

  throw new Error("Create availability method was not found in availabilityService.");
};

const safeCallUpdateAvailability = async (id, payload) => {
  if (typeof availabilityService.updateAvailabilityRule === "function") {
    return availabilityService.updateAvailabilityRule(id, payload);
  }

  if (typeof availabilityService.editAvailabilityRule === "function") {
    return availabilityService.editAvailabilityRule(id, payload);
  }

  throw new Error("Update availability method was not found in availabilityService.");
};

const safeCallDeleteAvailability = async (id) => {
  if (typeof availabilityService.deleteAvailabilityRule === "function") {
    return availabilityService.deleteAvailabilityRule(id);
  }

  if (typeof availabilityService.removeAvailabilityRule === "function") {
    return availabilityService.removeAvailabilityRule(id);
  }

  throw new Error("Delete availability method was not found in availabilityService.");
};

const normalizeRule = (rule) => {
  const normalizedDate = formatDateForApi(rule?.date);
  const expired = isPastAvailabilityDate(normalizedDate);

  return {
    ...rule,
    date: normalizedDate,
    startTime: rule?.startTime ? String(rule.startTime).slice(0, 5) : "",
    endTime: rule?.endTime ? String(rule.endTime).slice(0, 5) : "",
    isActive: expired ? false : rule?.isActive !== false,
  };
};

const ManageAvailability = () => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [search, setSearch] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form, setForm] = useState(initialFormState);
  const [formErrors, setFormErrors] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);

  const syncExpiredAvailabilityRules = useCallback(async (ruleList) => {
    const expiredActiveRules = ruleList.filter(
      (rule) => isPastAvailabilityDate(rule?.date) && rule?.isActive !== false
    );

    if (expiredActiveRules.length === 0) return;

    await Promise.all(
      expiredActiveRules.map((rule) =>
        safeCallUpdateAvailability(rule.id, {
          date: rule.date,
          startTime: rule.startTime,
          endTime: rule.endTime,
          isActive: false,
        })
      )
    );
  }, []);

  const loadRules = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const response = await safeCallGetAllAvailability();
      const data = response?.data || response;

      if (data?.statusCode && data.statusCode !== 200) {
        throw new Error(data.message || "Failed to load availability rules.");
      }

      const rawRules = Array.isArray(data?.availabilityRuleList)
        ? data.availabilityRuleList.map((rule) => ({
            ...rule,
            date: formatDateForApi(rule?.date),
            startTime: rule?.startTime ? String(rule.startTime).slice(0, 5) : "",
            endTime: rule?.endTime ? String(rule.endTime).slice(0, 5) : "",
            isActive: rule?.isActive !== false,
          }))
        : [];

      await syncExpiredAvailabilityRules(rawRules);

      const refreshedResponse = await safeCallGetAllAvailability();
      const refreshedData = refreshedResponse?.data || refreshedResponse;

      if (refreshedData?.statusCode && refreshedData.statusCode !== 200) {
        throw new Error(
          refreshedData.message || "Failed to refresh availability rules."
        );
      }

      const nextRules = Array.isArray(refreshedData?.availabilityRuleList)
        ? refreshedData.availabilityRuleList.map(normalizeRule)
        : [];

      setRules(nextRules);
    } catch (err) {
      setError(err?.message || "We could not load availability rules.");
    } finally {
      setLoading(false);
    }
  }, [syncExpiredAvailabilityRules]);

  useEffect(() => {
    loadRules();
  }, [loadRules]);

  const sortedRules = useMemo(() => {
    return [...rules].sort((a, b) => {
      const aDateTime = new Date(
        `${a?.date || "1970-01-01"}T${a?.startTime || "00:00"}`
      ).getTime();

      const bDateTime = new Date(
        `${b?.date || "1970-01-01"}T${b?.startTime || "00:00"}`
      ).getTime();

      return bDateTime - aDateTime;
    });
  }, [rules]);

  const filteredRules = useMemo(() => {
    const query = search.trim().toLowerCase();

    return sortedRules.filter((rule) => {
      const formattedDate = rule?.date ? formatDate(rule.date).toLowerCase() : "";

      const matchesSearch =
        !query ||
        formattedDate.includes(query) ||
        rule?.date?.toLowerCase().includes(query) ||
        rule?.startTime?.toLowerCase().includes(query) ||
        rule?.endTime?.toLowerCase().includes(query) ||
        (rule?.isActive !== false ? "active" : "inactive").includes(query);

      const matchesDate = !dateFilter || rule?.date === formatDateForApi(dateFilter);

      return matchesSearch && matchesDate;
    });
  }, [sortedRules, search, dateFilter]);

  const resetMessages = () => {
    setError("");
    setSuccessMessage("");
  };

  const openCreateModal = () => {
    resetMessages();
    setForm(initialFormState);
    setFormErrors({});
    setIsModalOpen(true);
  };

  const openEditModal = (rule) => {
    if (isPastAvailabilityDate(rule?.date)) return;

    resetMessages();
    setForm({
      id: rule?.id ?? null,
      date: formatDateForApi(rule?.date),
      startTime: rule?.startTime ? String(rule.startTime).slice(0, 5) : "",
      endTime: rule?.endTime ? String(rule.endTime).slice(0, 5) : "",
      isActive: rule?.isActive !== false,
    });
    setFormErrors({});
    setIsModalOpen(true);
  };

  const validateForm = () => {
    const nextErrors = {};

    if (isEmpty(form.date)) nextErrors.date = "Please choose a date.";
    if (isEmpty(form.startTime)) nextErrors.startTime = "Please choose a start time.";
    if (isEmpty(form.endTime)) nextErrors.endTime = "Please choose an end time.";

    if (form.startTime && !isTimeIncrementValid(form.startTime)) {
      nextErrors.startTime = "Start time must use 15-minute increments.";
    }

    if (form.endTime && !isTimeIncrementValid(form.endTime)) {
      nextErrors.endTime = "End time must use 15-minute increments.";
    }

    if (
      form.startTime &&
      form.endTime &&
      !isEndTimeAfterStartTime(form.startTime, form.endTime)
    ) {
      nextErrors.endTime = "End time must be later than the start time.";
    }

    if (form.date && isPastAvailabilityDate(form.date) && form.isActive) {
      nextErrors.date = "Past dates cannot remain active.";
    }

    return nextErrors;
  };

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    resetMessages();

    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    setFormErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    resetMessages();

    const nextErrors = validateForm();
    setFormErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) return;

    const shouldForceInactive = isPastAvailabilityDate(form.date);

    const payload = {
      date: formatDateForApi(form.date),
      startTime: form.startTime,
      endTime: form.endTime,
      isActive: shouldForceInactive ? false : Boolean(form.isActive),
    };

    try {
      setActionLoading(true);

      const response = form.id
        ? await safeCallUpdateAvailability(form.id, payload)
        : await safeCallCreateAvailability(payload);

      const data = response?.data || response;

      if (data?.statusCode && data.statusCode !== 200) {
        throw new Error(
          data.message ||
            (form.id
              ? "Failed to update availability."
              : "Failed to create availability.")
        );
      }

      setSuccessMessage(
        data?.message ||
          (form.id
            ? "Availability was updated successfully."
            : "Availability was created successfully.")
      );

      setIsModalOpen(false);
      setForm(initialFormState);
      setFormErrors({});

      await loadRules();
    } catch (err) {
      setError(err?.message || "We could not save availability.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget?.id) return;

    try {
      setActionLoading(true);
      resetMessages();

      const response = await safeCallDeleteAvailability(deleteTarget.id);
      const data = response?.data || response;

      if (data?.statusCode && data.statusCode !== 200) {
        throw new Error(data.message || "Failed to delete availability.");
      }

      setSuccessMessage(data?.message || "Availability was deleted successfully.");
      setDeleteTarget(null);

      await loadRules();
    } catch (err) {
      setError(err?.message || "We could not delete availability.");
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {loading ? <Loader text="Loading availability schedule..." /> : null}

      {error ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-4">
          <p className="text-sm font-medium text-red-700">{error}</p>
        </div>
      ) : null}

      {successMessage ? (
        <div className="rounded-2xl border border-green-200 bg-green-50 p-4">
          <p className="text-sm font-medium text-green-700">{successMessage}</p>
        </div>
      ) : null}

      <AdminCard
        title="Manage Availability"
        subtitle="Set available dates and time windows so bookings stay aligned with the artist’s schedule."
        actions={
          <div className="flex flex-col gap-3 md:flex-row">
            <Input
              name="availabilitySearch"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search by date, time, or status..."
              className="min-w-[220px]"
            />

            <Input
              name="dateFilter"
              type="date"
              value={dateFilter}
              onChange={(event) => setDateFilter(event.target.value)}
            />

            <Button type="button" variant="primary" onClick={openCreateModal}>
              Add Availability
            </Button>
          </div>
        }
      >
        <AdminTable
          loading={loading}
          data={filteredRules}
          emptyMessage="No availability entries match the current filters."
          className="max-h-[270px] overflow-y-auto"
          columns={[
            {
              key: "date",
              label: "Date",
              render: (row) => (row?.date ? formatDate(row.date) : "—"),
            },
            {
              key: "startTime",
              label: "Start Time",
              render: (row) =>
                row?.startTime ? String(row.startTime).slice(0, 5) : "—",
            },
            {
              key: "endTime",
              label: "End Time",
              render: (row) =>
                row?.endTime ? String(row.endTime).slice(0, 5) : "—",
            },
            {
              key: "isActive",
              label: "Status",
              render: (row) =>
                row?.isActive !== false ? (
                  <span className="status-chip status-chip-confirmed">ACTIVE</span>
                ) : (
                  <span className="status-chip status-chip-canceled">INACTIVE</span>
                ),
            },
          ]}
          actions={(row) => {
            const isExpired = isPastAvailabilityDate(row?.date);
            const actions = [];

            if (!isExpired) {
              actions.push({
                label: "Edit",
                variant: "secondary",
                onClick: () => openEditModal(row),
              });
            }

            actions.push({
              label: "Delete",
              variant: "danger",
              onClick: () => setDeleteTarget(row),
            });

            return actions;
          }}
        />
      </AdminCard>

      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          if (actionLoading) return;
          setIsModalOpen(false);
        }}
        title={form.id ? "Edit Availability" : "Create Availability"}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Date"
            name="date"
            type="date"
            value={form.date}
            onChange={handleChange}
            error={formErrors.date}
            required
          />

          <div className="grid gap-4 md:grid-cols-2">
            <Input
              label="Start Time"
              name="startTime"
              type="time"
              value={form.startTime}
              onChange={handleChange}
              error={formErrors.startTime}
              required
            />

            <Input
              label="End Time"
              name="endTime"
              type="time"
              value={form.endTime}
              onChange={handleChange}
              error={formErrors.endTime}
              required
            />
          </div>

          <label className="flex items-center gap-3 text-sm font-medium text-[#4b3a33]">
            <input
              type="checkbox"
              name="isActive"
              checked={form.isActive}
              onChange={handleChange}
              className="h-4 w-4 rounded border-[#d7c2a3] text-henna focus:ring-[#ead8c0]"
            />
            Keep this time window active
          </label>

          <div className="flex justify-end gap-3 pt-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setIsModalOpen(false)}
              disabled={actionLoading}
            >
              Cancel
            </Button>

            <Button type="submit" variant="primary" loading={actionLoading}>
              {form.id ? "Save Changes" : "Create Availability"}
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={Boolean(deleteTarget)}
        onClose={() => {
          if (actionLoading) return;
          setDeleteTarget(null);
        }}
        title="Delete Availability"
      >
        <div className="space-y-4">
          <p className="text-sm text-muted-warm">
            Are you sure you want to remove this availability entry?
          </p>

          {deleteTarget?.date ? (
            <div className="rounded-2xl bg-soft-cream p-4 text-sm text-[#4b3a33]">
              <p>
                <span className="font-semibold">Date:</span> {formatDate(deleteTarget.date)}
              </p>
              <p>
                <span className="font-semibold">Time:</span>{" "}
                {deleteTarget.startTime} - {deleteTarget.endTime}
              </p>
            </div>
          ) : null}

          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setDeleteTarget(null)}
              disabled={actionLoading}
            >
              Cancel
            </Button>

            <Button
              type="button"
              variant="danger"
              onClick={handleDelete}
              loading={actionLoading}
            >
              Delete
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ManageAvailability;